import { Dispatch, memo, SetStateAction, useMemo } from "react";
import { Button, FCX, getRem, ID, Tooltip } from "@datassential/platform-ui-lib";
import VersusScatterChart from "@vms/VersusScatterChart";
import Popup from "@components/Popup";
import ItemTile from "@vms/ItemTile";
import { KEYWORD_TILE_METRICS_ORDER } from "@models/Metric";
import { VisualizeData, VisualizeMetric } from "@api/useVisualizeData";
import { VersusScatterChartItem } from "@vms/VersusScatterChart/VersusScatterChart";
import { useThemeContext } from "../../App";
import { CHART_ID } from "@pages/Visualize/Visualize";
import {
    useVisualizeContext,
    VISUALIZE_VIEWS,
    VISUALIZE_VIEWS_ORDER,
    VisualizeView
} from "@pages/Visualize/VisualizeContext";
import Field from "@components/Field";
import cn from "classnames";
import { MAC_STAGE_COLORS, MacStage } from "@models/MacStage";
import { useGetKeywordBaseData } from "@api/useGetKeywordBaseData";
import { HeatScore } from "@models/HeatScore";

type ChartItem = VersusScatterChartItem<VisualizeMetric>;

const ViewButton: FCX<{
    viewId: VisualizeView;
}> = ({
    viewId,
}) => {
    const {
        setActiveView,
        activeView,
    } = useVisualizeContext();
    const view = useMemo(
        () => VISUALIZE_VIEWS.get(viewId)!,
        [viewId]
    );

    const isActive = useMemo(() => activeView === viewId, [activeView, viewId]);

    return (
        <Button
            className={cn(
                "VisualizeFilters__preset-button",
                { "is-active": isActive }
            )}
            style={{ pointerEvents: isActive ? "none" : undefined, }}
            onClick={() => setActiveView(viewId)}
        >
            {view.name}
        </Button>
    );
}

const VisualizeChart: FCX<{
    data: VisualizeData;
    activeItemId: ID | null;
    setActiveItemId: Dispatch<SetStateAction<ID | null>>;
}> = ({
    data: rawData,
    activeItemId,
    setActiveItemId,
}) => {
    const {
        xMetricId,
        setXMetricId,
        yMetricId,
        setYMetricId,
        activeView,
    } = useVisualizeContext();

    const { isLightThemeEnabled } = useThemeContext();

    const getKeywordBaseData = useGetKeywordBaseData();

    const metrics = useMemo(
        () => {
            if (rawData.length === 0) {
                return [];
            }

            return rawData[0].metrics.map(metric => ({
                id: metric.id,
                title: metric.name,
            }));
        },
        [rawData]
    );

    const data = useMemo(
        (): VersusScatterChartItem<VisualizeMetric>[] => {
            return rawData.map(item => {
                const values: ChartItem['values'] = new Map(item.metrics.map(metric => [
                    metric.id,
                    metric.sortValue,
                ]));

                const printValues: ChartItem['printValues'] = new Map(item.metrics.map(metric => [
                    metric.id,
                    metric.value ?? 0,
                ]));

                return {
                    id: item.id,
                    color: MAC_STAGE_COLORS[(item.macStage as MacStage) ?? MacStage.NotSelected].color,
                    values,
                    printValues,
                };
            });
        },
        [rawData]
    );

    const activeItem = useMemo(
        () =>
            document.querySelector(`[data-tooltip-id=scatter-chart-item-${activeItemId}]`)
                ? data.find(item => item.id === activeItemId)
                : undefined,
        [activeItemId, data]
    );

    const extendedActiveItem = useMemo(() => {
        const keywordData = getKeywordBaseData(+(activeItem?.id ?? 0))

        return {
            ...keywordData,
            description: keywordData?.description ?? "",
            macStage: keywordData?.macStage ?? MacStage.NotSelected,
            heatScore: keywordData?.heatScore === HeatScore.NoData ? null : keywordData?.heatScore,
            id: keywordData.id,
            metrics: KEYWORD_TILE_METRICS_ORDER.map((metricId) => {
                const metric = metrics.find((metric) => metric.id === metricId)

                return {
                    id: metricId,
                    name: metric?.title ?? "",
                    value: activeItem?.printValues.get(metricId) ?? null,
                }
            })
        }
    }, [activeItem, getKeywordBaseData, metrics])

    const chartAccentItemsIds = useMemo(
        () => {
            return rawData
                .filter(i => {
                    if (activeView === VisualizeView.FamiliarFavorites) return i.isFamiliarFavorites;
                    if (activeView === VisualizeView.RisingStars) return i.isRisingStars;
                    if (activeView === VisualizeView.Polarity) return i.isPolarValue;
                    return false;
                })
                .map(i => i.id);
        },
        [rawData, activeView]
    );

	return (
        <>
            <div className="VisualizeFilters">
                <Field label="VIEW:" withoutBox>
                    {VISUALIZE_VIEWS_ORDER.map((viewId) => (
                        <ViewButton key={`view-button-${viewId}`} viewId={viewId} />
                    ))}
                </Field>
            </div>
            <VersusScatterChart
                id={CHART_ID}
                data={data}
                accentItemsIds={chartAccentItemsIds}
                options={metrics}
                xOptionId={xMetricId}
                setXOptionId={setXMetricId}
                yOptionId={yMetricId}
                setYOptionId={setYMetricId}
                setActiveItemId={setActiveItemId}
            />
            {activeItem && (
                <Popup>
                    <Tooltip
                        isOpen
                        id={`scatter-chart-item-${activeItem.id}`}
                        style={{
                            outlineColor: activeItem.color ?? '#7cb5ec',
                            outlineWidth: getRem(4),
                            outlineOffset: getRem(0),
                            outlineStyle: "solid",
                            padding: 0,
                            width: getRem(280),
                        }}
                        place="left"
                        arrowColor={isLightThemeEnabled ? "#fff" : "#293545"}
                        variant="dark"
                    >
                        <ItemTile
                            item={extendedActiveItem}
                            isMinimized
                        />
                    </Tooltip>
                </Popup>
            )}
        </>
    );
};

export default memo(VisualizeChart);